/**
 * Module dependencies.
 */

import { ReactElement, useEffect } from 'react';
import { Type } from 'src/components/core';
import { color, media, units } from '@untile/react-components';
import { useAdvisor } from 'src/context/advisor/context';
import { useLocomotiveScroll } from 'react-locomotive-scroll';
import ContactForm from './form';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  height: 100%;
  padding-bottom: ${units(6)};
  position: relative;
  width: 100%;

  ${media.min('md')`
    display: grid;
    grid-template-areas:
      '. title       title       .'
      '. description description .'
      '. form        .           .';
    grid-template-columns: calc(1 / 8 * 100%) calc(4 / 8 * 100%) calc(1 / 8 * 100%) calc(2 / 8 * 100%);
    padding-bottom: ${units(14)};
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H4)`
  color: ${color('black')};
  grid-area: title;
  margin-bottom: ${units(1)};
`;

/**
 * `Description` styled component.
 */

const Description = styled(Type.Paragraph)`
  color: ${color('grey500')};
  grid-area: description;
  margin-bottom: ${units(6)};
`;

/**
 * `StyledContactForm` styled component.
 */

const StyledContactForm = styled(ContactForm)`
  grid-area: form;

  ${media.max('md')`
    margin-bottom: ${units(5)};
  `}
`;

/**
 * `ContactFormStep` component.
 */

const ContactFormStep = (props: Props): ReactElement => {
  const { className } = props;
  const { translate } = useTranslate();
  const { visibleStep } = useAdvisor();
  const locomotiveScroll = useLocomotiveScroll();

  useEffect(() => {
    if (locomotiveScroll?.scroll) {
      locomotiveScroll.scroll.scrollTo('top', { duration: 200 });
    }
  }, [locomotiveScroll.scroll, visibleStep]);

  return (
    <Wrapper className={className}>
      <Title>{translate('advisor:steps.contactForm.title')}</Title>

      <Description>
        {translate('advisor:steps.contactForm.description')}
      </Description>

      <StyledContactForm />
    </Wrapper>
  );
};

/**
 * Export `ContactFormStep` component.
 */

export default ContactFormStep;
